// In your Navbar.jsx file
import React, { useEffect, useState } from 'react';
import { Menu, Image, Dropdown, Label } from 'semantic-ui-react';
import NotificationCenter from './NotificationCenter';

// Get the logo path - works in both dev and production
// In Create React App, files in public folder are served from root
const LOGO_PATH = process.env.PUBLIC_URL 
    ? `${process.env.PUBLIC_URL}/logo192.png` 
    : '/logo192.png';

const Navbar = ({ user, roles = [], onLogout, onSettingsClick, onProfileClick, onViewAllEvents }) => {
    const [scrolled, setScrolled] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
        const stored = localStorage.getItem('tb_notificationsEnabled');
        return stored !== null ? stored === 'true' : true;
    });

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 4);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Listen for notifications preference changes from Settings page
    useEffect(() => {
        const handleNotificationsChange = (e) => {
            setNotificationsEnabled(e.detail.enabled);
        };
        window.addEventListener('tb:notificationsEnabledChanged', handleNotificationsChange);
        return () => {
            window.removeEventListener('tb:notificationsEnabledChanged', handleNotificationsChange);
        };
    }, []);

    const getRoleColor = (role) => {
        switch (role) {
            case 'ST_FORM_FILLER':
                return '#35B7F4'; // Secondary
            case 'MT_FORM_FILLER':
                return '#4F46E5'; // Indigo
            case 'MT_DECOMMISSION_REQUESTER':
                return '#0EA5E9';
            case 'MT_DECOMMISSION_APPROVER':
                return '#14B8A6';
            case 'DEVOPS':
                return '#475569'; // Slate/Dark Gray
            case 'LICENSE_GENERATOR':
                return '#F97316'; // Orange
            case 'APPROVER':
                return '#6F9911'; // Green
            case 'CAPACITY_READER':
                return '#9CA3AF'; // Light gray
            default:
                return '#6B7280'; // Default gray
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'ST_FORM_FILLER':
                return 'ST FORM FILLER';
            case 'MT_FORM_FILLER':
                return 'MT FORM FILLER';
            case 'MT_DECOMMISSION_REQUESTER':
                return 'MT DECOMMISSION REQUESTER';
            case 'MT_DECOMMISSION_APPROVER':
                return 'MT DECOMMISSION APPROVER';
            case 'LICENSE_GENERATOR':
                return 'LICENSE GENERATOR';
            case 'CAPACITY_READER':
                return 'CAPACITY READER';
            default:
                return role.replace(/_/g, ' ');
        }
    };

    return (
        <Menu fixed="top" inverted className={`app-navbar${scrolled ? ' scrolled' : ''}`}>
            <Menu.Item header>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img 
                        src={LOGO_PATH} 
                        alt="tenantBridge Logo" 
                        style={{ 
                            height: '28px', 
                            width: '28px',
                            display: 'block'
                        }}
                        onError={(e) => {
                            // Fallback: try absolute path if PUBLIC_URL path fails
                            const currentSrc = e.target.src;
                            const absolutePath = window.location.origin + '/logo192.png';
                            if (currentSrc !== absolutePath) {
                                e.target.src = absolutePath;
                            } else {
                                // If both fail, hide the image gracefully
                                e.target.style.display = 'none';
                            }
                        }}
                    />
                    <span>tenantBridge</span>
                </div>
            </Menu.Item>

            {user && (
                <Menu.Menu position='right'>
                    <Menu.Item style={{ padding: 0, display: 'flex', alignItems: 'center' }}>
                        <NotificationCenter 
                            userEmail={user.email} 
                            user={user} 
                            roles={roles}
                            notificationsEnabled={notificationsEnabled} 
                        />
                    </Menu.Item>
                    <Menu.Item>
                        {/* Avatar with fallback */}
                        {user?.picture ? (
                            <Image
                                src={user.picture}
                                avatar
                                style={{ marginRight: '10px' }}
                                referrerPolicy='no-referrer'
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    const holder = document.createElement('div');
                                    holder.style.width = '30px';
                                    holder.style.height = '30px';
                                    holder.style.borderRadius = '50%';
                                    holder.style.backgroundColor = '#f8f9fa';
                                    holder.style.display = 'inline-flex';
                                    holder.style.alignItems = 'center';
                                    holder.style.justifyContent = 'center';
                                    holder.style.border = '1px solid #e9ecef';
                                    holder.style.marginRight = '10px';
                                    holder.innerHTML = `
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6c757d" stroke-width="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                    `;
                                    e.target.parentElement.insertBefore(holder, e.target);
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                backgroundColor: '#f8f9fa',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #e9ecef',
                                marginRight: '10px'
                            }}>
                                {user?.name ? (
                                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#6c757d' }}>
                                        {user.name.charAt(0).toUpperCase()}
                                    </span>
                                ) : (
                                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#6c757d' strokeWidth='2'>
                                        <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'></path>
                                        <circle cx='12' cy='7' r='4'></circle>
                                    </svg>
                                )}
                            </div>
                        )}

                        <Dropdown text={user.name}>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={onProfileClick} style={{ cursor: 'pointer', padding: '1rem 1.2rem' }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.75rem',
                                            backgroundColor: '#F9FAFB',
                                            borderRadius: '0.75rem',
                                            border: '1px solid #E5E7EB',
                                            padding: '0.9rem 1rem'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {user?.picture ? (
                                                <img
                                                    src={user.picture}
                                                    alt="Profile"
                                                    referrerPolicy="no-referrer"
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '50%',
                                                        objectFit: 'cover',
                                                        border: '2px solid #E5E7EB'
                                                    }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#062D4D',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: '16px'
                                                }}>
                                                    {user?.name?.charAt(0).toUpperCase() || '?'}
                                                </div>
                                            )}
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#062D4D', fontSize: '0.95rem' }}>{user.name}</div>
                                                <div style={{ fontSize: '0.83em', color: '#6B7280', marginTop: '2px' }}>{user.email}</div>
                                            </div>
                                        </div>
                                        {!!roles?.length && (
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                                    rowGap: '0.4rem',
                                                    columnGap: '0.4rem',
                                                    maxWidth: '320px'
                                                }}
                                            >
                                                {(roles || []).slice(0, 9).map((role, index) => (
                                                    <Label
                                                        key={index}
                                                        size='tiny'
                                                        style={{ 
                                                            marginRight: 0,
                                                            backgroundColor: getRoleColor(role),
                                                            color: '#FFFFFF',
                                                            borderColor: getRoleColor(role),
                                                            fontWeight: 500,
                                                            letterSpacing: '0.02em',
                                                            textAlign: 'center',
                                                            justifyContent: 'center',
                                                            padding: '0.4em 0.5em',
                                                            fontSize: '0.72rem',
                                                            lineHeight: '1.2',
                                                            whiteSpace: 'normal',
                                                            ...(role === 'APPROVER'
                                                                ? { display: 'flex', alignItems: 'center' }
                                                                : {})
                                                        }}
                                                    >
                                                        {getRoleLabel(role)}
                                                    </Label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Dropdown.Item>
                                <Dropdown.Item style={{ cursor: 'default' }}>
                                    <div style={{ 
                                        fontSize: '0.85em', 
                                        color: '#6b7280'
                                    }}>
                                        Version: v3.1.1 • Released: June 01, 2026
                                    </div>
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                    icon='cog'
                                    text='Settings'
                                    onClick={onSettingsClick}
                                />
                                {roles.includes('DEVOPS') && onViewAllEvents && (
                                    <>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                            icon='list'
                                            text='All Events'
                                            onClick={onViewAllEvents}
                                        />
                                    </>
                                )}
                                <Dropdown.Divider />
                                <Dropdown.Item
                                    text='Sign Out'
                                    icon='sign out'
                                    onClick={onLogout}
                                />
                            </Dropdown.Menu>
                        </Dropdown>
                    </Menu.Item>
                </Menu.Menu>
            )}
        </Menu>
    );
};

export default Navbar;